import { QuestionProps } from "./ChecklistQuestionTypes"
import { ChecklistQuestionOptionInput } from "./ChecklistQuestionOptionInput"
import { FunctionComponent, useState } from "react"
import { IconButton, RadioButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { ToggleSwitch } from "@nova/component-toggle-switch"

export const ChecklistRadioQuestion = ({
    questionControls,
    spec,
    autoFocus = false,
}: QuestionProps<"radio">) => {
    // Index so that newly added questions are focused/editable automatically
    const [autofocusOptionIndex, setAutofocusOptionIndex] = useState<number>()

    const handleNewQuestionClick = () => {
        questionControls.editQuestion(spec.id, {
            ...spec,
            options: [...spec.options, `Option ${spec.options.length + 1}`],
        })
        setAutofocusOptionIndex(spec.options.length)
    }

    const deleteOption = (index: number) => {
        questionControls.editQuestion(spec.id, {
            ...spec,
            options: spec.options.filter((v, i) => i !== index),
        })
    }

    const moveOption = (index: number, direction: -1 | 1) => {
        const element = spec.options[index]
        const copy = [...spec.options]
        copy.splice(index, 1)
        copy.splice(Math.max(index + direction, 0), 0, element)
        questionControls.editQuestion(spec.id, { ...spec, options: copy })
    }

    return (
        <div className="flex flex-col gap-2">
            <input
                className="bg-gray-700 rounded-lg text-gray-0 border-0"
                value={spec.question}
                onChange={(e) =>
                    questionControls.editQuestion(spec.id, { ...spec, question: e.target.value })
                }
                placeholder="Enter a label"
                autoFocus={autoFocus}
            />
            <div className="flex flex-col gap-1">
                {spec.options.map((option, i) => (
                    <RadioOption
                        index={i}
                        numOptions={spec.options.length}
                        onChange={(val) => {
                            questionControls.editQuestion(spec.id, {
                                ...spec,
                                options: spec.options.map((o, oi) => (oi === i ? val : o)),
                            })
                            setAutofocusOptionIndex(undefined)
                        }}
                        onDelete={() => deleteOption(i)}
                        onMove={(direction) => moveOption(i, direction)}
                        value={option}
                        autoFocus={autofocusOptionIndex === i}
                    />
                ))}
            </div>
            <div className="flex gap-2 items-center" onClick={handleNewQuestionClick}>
                <RadioButton isSelected={false} onClick={() => {}} />
                <h5 className="text-gray-400">Add option</h5>
            </div>
        </div>
    )
}

interface RadioOptionProps {
    value: string
    index: number
    numOptions: number
    onChange: (val: string) => void
    onDelete: () => void
    onMove: (direction: 1 | -1) => void
    autoFocus: boolean
}

const RadioOption: FunctionComponent<RadioOptionProps> = ({
    value,
    index,
    numOptions,
    onChange,
    onDelete,
    onMove,
    autoFocus,
}) => {
    const [isEditing, setIsEditing] = useState(autoFocus)
    return (
        <div className="flex gap-2 items-center group/option">
            <RadioButton isSelected={false} onClick={() => {}} />
            {isEditing ? (
                <ChecklistQuestionOptionInput
                    initialValue={value}
                    onCancel={() => setIsEditing(false)}
                    onSubmit={(val) => {
                        onChange(val)
                        setIsEditing(false)
                    }}
                />
            ) : (
                <div className="flex items-center gap-3">
                    <h5
                        onClick={() => setIsEditing((prev) => !prev)}
                        className="text-white py-1.5 border-b border-b-transparent group-hover/option:border-b-gray-700 min-w-[111px]"
                    >
                        {value}
                    </h5>

                    <div className="flex items-center gap-2 opacity-0 group-hover/option:opacity-100 text-gray-100">
                        <IconButton className="hover:!text-red-500" onClick={() => onDelete()}>
                            <Icons.SmallIcon Icon={Icons.Trash} />
                        </IconButton>
                        <span className="flex items-center pl-2 border-l border-l-gray-700">
                            <IconButton onClick={() => onMove(-1)} disabled={index === 0}>
                                <Icons.Icon Icon={Icons.ChevronDown} />
                            </IconButton>
                            <IconButton
                                onClick={() => onMove(1)}
                                disabled={index + 1 === numOptions}
                            >
                                <Icons.Icon Icon={Icons.ChevronUp} />
                            </IconButton>
                        </span>
                    </div>
                </div>
            )}
        </div>
    )
}

export const ChecklistRadioFooter = ({ spec, questionControls }: QuestionProps<"radio">) => {
    return (
        <div className="px-8 py-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
                <ToggleSwitch
                    isToggled={spec.isRequired}
                    setIsToggled={(v) =>
                        questionControls.editQuestion(spec.id, {
                            ...spec,
                            isRequired: v,
                        })
                    }
                />
                <div>Required</div>
            </div>
            <div className="grow" />
            <IconButton
                onClick={() => {
                    questionControls.deleteQuestion(spec.id)
                }}
            >
                <Icons.Icon
                    Icon={Icons.Trash}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                />
            </IconButton>
        </div>
    )
}
